import { authFetch, getBackendUser, onAuthChange } from './auth.js';
import { modalOpen, modalClose } from './modal-state.js';
import { DATE_LOCALE } from './date-locale.js';

const apiUrl = import.meta.env.VITE_API_BASE_URL;

// The feeder service polls at least every ~25s (FEEDER_POLL_MAX_WAIT_SECONDS), so a few
// missed polls in a row means the Pi is gone.
const ONLINE_THRESHOLD_MS = 2 * 60 * 1000;

// While the modal is open the Pi stats are refetched this often. Matches the fan service's
// 30s telemetry report.
const STATS_REFRESH_MS = 30 * 1000;

// Only these keys are merged from a stats refresh, so it never moves the baseline that
// unsaved form edits are diffed against.
const STATS_KEYS = ['cpu_temperature', 'fan_speed_percent', 'feeder_last_seen_at'];

// How long the result of an admin action stays on screen after the modal closes.
const TOAST_DURATION_MS = 3000;

const NO_PERMISSION_MESSAGE = 'you do not have permission to change this parameter';

const DEFAULT_SECTION = 'details';

// Map: response field name -> request field name (only when they differ).
const REQUEST_FIELD_NAME = {
  feeder_cooldown_time: 'feed_cooldown_time',
};

// All editable fields saved with [save changes], in the order they appear in the form.
// Light intensity is not one of them: its slider applies the change as soon as it is released.
const FIELDS = [
  { id: 'roomAdminDisplayName',         responseKey: 'display_name',                              type: 'string',  required: true,  maxLength: 100 },
  { id: 'roomAdminDescription',         responseKey: 'description',                               type: 'string',  required: true,  maxLength: 5000 },
  { id: 'roomAdminBanner',             responseKey: 'banner',                                    type: 'string',  maxLength: 5000 },
  { id: 'roomAdminBannerVisible',      responseKey: 'banner_visible',                            type: 'bool' },
  { id: 'roomAdminCity',                responseKey: 'city',                                      type: 'string',  maxLength: 120 },
  { id: 'roomAdminCountry',             responseKey: 'country',                                   type: 'string',  maxLength: 120 },
  { id: 'roomAdminFeedCooldown',        responseKey: 'feeder_cooldown_time',                      type: 'int',     min: 0 },
  { id: 'roomAdminVisible',             responseKey: 'visible',                                   type: 'bool' },
  { id: 'roomAdminMessagePermission',   responseKey: 'message_permission',                        type: 'string' },
  { id: 'roomAdminFeedingPermission',    responseKey: 'feeding_permission',                        type: 'string' },
  { id: 'roomAdminLightAlwaysOn',       responseKey: 'light_always_on',                           type: 'bool' },
  { id: 'roomAdminLightOnFeed',        responseKey: 'turn_on_light_on_feed_enabled',              type: 'bool' },
  { id: 'roomAdminLightOnSighting',    responseKey: 'turn_on_light_on_cat_sighting_enabled',      type: 'bool' },
  { id: 'roomAdminLightFadeSeconds',   responseKey: 'light_fade_seconds',                        type: 'int', min: 0, max: 10, raspberryOnly: true },
  { id: 'roomAdminNightStart',          responseKey: 'night_schedule_start_hour',                  type: 'nullable_int', min: 0, max: 23 },
  { id: 'roomAdminNightEnd',            responseKey: 'night_schedule_end_hour',                    type: 'nullable_int', min: 0, max: 23 },
  { id: 'roomAdminLightOnStart',        responseKey: 'light_on_start_hour',                        type: 'nullable_int', min: 0, max: 23 },
  { id: 'roomAdminLightOnEnd',          responseKey: 'light_on_end_hour',                          type: 'nullable_int', min: 0, max: 23 },
];

// Detection config lives in a nested `detection_config` object on the room and is
// saved through a dedicated endpoint (PATCH /rooms/{name}/detection-config).
// The on/off switch is read from the room itself (`fromRoom`): the nested
// `detection_enabled` only says whether the Pi is detecting.
// `showWhen` fields only show, count as edits and get saved while detection is on
// ('enabled'), or on with local detection ('LOCAL').
const DETECTION_FIELDS = [
  { id: 'roomAdminDetectionEnabled',      key: 'detection_enabled',        type: 'bool',   fromRoom: true },
  { id: 'roomAdminDetectionPipeline',     key: 'detection_pipeline',       type: 'string', showWhen: 'enabled' },
  { id: 'roomAdminConfidenceThreshold',   key: 'confidence_threshold',     type: 'float', min: 0, max: 1, showWhen: 'LOCAL' },
  { id: 'roomAdminSnapshotTime',          key: 'snapshot_time',            type: 'float', min: 0,         showWhen: 'LOCAL' },
  { id: 'roomAdminCatLeftMargin',         key: 'cat_left_seconds_margin',  type: 'float', min: 0,         showWhen: 'LOCAL' },
  { id: 'roomAdminMaxVideoDuration',      key: 'max_video_duration',       type: 'int',   exclusiveMin: 0, showWhen: 'LOCAL' },
  { id: 'roomAdminMotionThreshold',       key: 'motion_threshold',         type: 'int',   min: 0,         showWhen: 'LOCAL' },
  { id: 'roomAdminCheckInterval',         key: 'check_interval',           type: 'float', exclusiveMin: 0, showWhen: 'LOCAL' },
];

let activeStream = null;
let onUpdated = null;
let modalEl = null;
let formEl = null;
let feedbackEl = null;
let saveBtn = null;
let cancelBtn = null;
let actionsEl = null;
let fieldsEl = null;
let gearBtn = null;
let roomNameEl = null;
let titleEl = null;
let cpuTempToggleBtn = null;
let fanSpeedStatEl = null;
let unsavedModalEl = null;
let noPermissionModalEl = null;
let silentFeedModalEl = null;
let toastEl = null;
let toastTimer = null;
let closeBtn = null;
let turnOnLightBtn = null;
let turnOffLightBtn = null;
let resetCooldownBtn = null;
let silentFeedBtn = null;
let descriptionCountEl = null;
let lightAlwaysOnEl = null;
let lightOnFeedEl = null;
let lightOnSightingEl = null;
let detectionEnabledEl = null;
let detectionPipelineEl = null;
let detectionPipelineFieldEl = null;
let detectionAdvancedEl = null;
let centralDetectionSettingsEl = null;
let raspberryLightControlsEl = null;
let raspberryStatsEl = null;
let cpuTempEl = null;
let cpuTempWarnEl = null;
let fanSpeedEl = null;
let statusEl = null;
let statusDotEl = null;
let lightIntensityEl = null;
let lightIntensityValueEl = null;
let lightIntensitySending = false;
let lightIntensityPending = null;
let statsRefreshTimer = null;
let submittingState = false;
let escHandler = null;
let initialized = false;

function getModalEls() {
  if (modalEl) return;
  modalEl = document.getElementById('roomAdminModal');
  formEl = document.getElementById('roomAdminForm');
  feedbackEl = document.getElementById('roomAdminFeedback');
  saveBtn = document.getElementById('roomAdminSave');
  cancelBtn = document.getElementById('roomAdminCancel');
  actionsEl = document.getElementById('roomAdminActions');
  fieldsEl = document.getElementById('roomAdminFields');
  gearBtn = document.getElementById('roomAdminGear');
  roomNameEl = document.getElementById('roomName');
  titleEl = document.getElementById('roomAdminTitle');
  cpuTempToggleBtn = document.getElementById('roomAdminCpuTempToggle');
  fanSpeedStatEl = document.getElementById('roomAdminFanSpeedStat');
  unsavedModalEl = document.getElementById('roomAdminUnsavedModal');
  noPermissionModalEl = document.getElementById('roomAdminNoPermissionModal');
  silentFeedModalEl = document.getElementById('roomAdminSilentFeedModal');
  toastEl = document.getElementById('roomAdminToast');
  closeBtn = document.getElementById('roomAdminClose');
  turnOnLightBtn = document.getElementById('roomAdminTurnOnLight');
  turnOffLightBtn = document.getElementById('roomAdminTurnOffLight');
  resetCooldownBtn = document.getElementById('roomAdminResetCooldown');
  silentFeedBtn = document.getElementById('roomAdminSilentFeed');
  descriptionCountEl = document.getElementById('roomAdminDescriptionCount');
  lightAlwaysOnEl = document.getElementById('roomAdminLightAlwaysOn');
  lightOnFeedEl = document.getElementById('roomAdminLightOnFeed');
  lightOnSightingEl = document.getElementById('roomAdminLightOnSighting');
  detectionEnabledEl = document.getElementById('roomAdminDetectionEnabled');
  detectionPipelineEl = document.getElementById('roomAdminDetectionPipeline');
  detectionPipelineFieldEl = document.getElementById('roomAdminDetectionPipelineField');
  detectionAdvancedEl = document.getElementById('roomAdminDetectionAdvanced');
  centralDetectionSettingsEl = document.getElementById('roomAdminCentralDetectionSettings');
  raspberryLightControlsEl = document.getElementById('roomAdminRaspberryLightControls');
  raspberryStatsEl = document.getElementById('roomAdminRaspberryStats');
  cpuTempEl = document.getElementById('roomAdminCpuTemp');
  cpuTempWarnEl = document.getElementById('roomAdminCpuTempWarn');
  fanSpeedEl = document.getElementById('roomAdminFanSpeed');
  statusEl = document.getElementById('roomAdminStatus');
  statusDotEl = document.getElementById('roomAdminStatusDot');
  lightIntensityEl = document.getElementById('roomAdminLightIntensity');
  lightIntensityValueEl = document.getElementById('roomAdminLightIntensityValue');
}

function init() {
  if (initialized) return;
  getModalEls();
  if (!modalEl || !formEl) return;
  initialized = true;

  gearBtn?.addEventListener('click', (event) => {
    event.stopPropagation();
    openModal();
  });

  // The room name opens the settings too, but only while it is marked as admin-clickable.
  roomNameEl?.addEventListener('click', (event) => {
    if (!roomNameEl.classList.contains('room-name--admin')) return;
    event.stopPropagation();
    openModal();
  });

  cpuTempToggleBtn?.addEventListener('click', () => setFanSpeedVisible(fanSpeedStatEl?.hidden === true));

  closeBtn?.addEventListener('click', requestClose);
  cancelBtn?.addEventListener('click', requestClose);

  modalEl.addEventListener('click', (event) => {
    if (event.target === modalEl) requestClose();
  });

  escHandler = (event) => {
    if (event.key !== 'Escape') return;
    if (isPopupOpen(noPermissionModalEl)) {
      closePopup(noPermissionModalEl);
    } else if (isPopupOpen(silentFeedModalEl)) {
      closePopup(silentFeedModalEl);
    } else if (isPopupOpen(unsavedModalEl)) {
      closePopup(unsavedModalEl);
    } else if (modalEl.classList.contains('active')) {
      requestClose();
    }
  };
  document.addEventListener('keydown', escHandler);

  formEl.addEventListener('submit', handleSubmit);
  formEl.addEventListener('input', syncDirtyState);
  formEl.addEventListener('change', syncDirtyState);
  turnOnLightBtn?.addEventListener('click', () => handleLightAction('turn-on-light', 'light turned on'));
  turnOffLightBtn?.addEventListener('click', () => handleLightAction('turn-off-light', 'light turned off'));
  resetCooldownBtn?.addEventListener('click', handleResetCooldown);
  silentFeedBtn?.addEventListener('click', () => openPopup(silentFeedModalEl));

  modalEl.querySelectorAll('.room-admin-tabs .modal-tab').forEach((tab) => {
    tab.addEventListener('click', () => showSection(tab.dataset.section));
  });

  // Touch screens can't hover, so tapping a locked control explains why it is locked.
  modalEl.addEventListener('click', (event) => {
    const locked = event.target.closest?.('.room-admin-locked');
    if (!locked || !modalEl.contains(locked)) return;
    event.preventDefault();
    if (window.matchMedia('(hover: none)').matches) openPopup(noPermissionModalEl);
  });

  document.getElementById('roomAdminUnsavedSave')?.addEventListener('click', () => {
    closePopup(unsavedModalEl);
    formEl.requestSubmit();
  });
  document.getElementById('roomAdminUnsavedExit')?.addEventListener('click', () => {
    closePopup(unsavedModalEl);
    closeModal();
  });
  document.getElementById('roomAdminNoPermissionOk')?.addEventListener('click', () => closePopup(noPermissionModalEl));
  document.getElementById('roomAdminSilentFeedYes')?.addEventListener('click', () => {
    closePopup(silentFeedModalEl);
    handleSilentFeed();
  });
  document.getElementById('roomAdminSilentFeedCancel')?.addEventListener('click', () => closePopup(silentFeedModalEl));
  [unsavedModalEl, noPermissionModalEl, silentFeedModalEl].forEach((popup) => {
    popup?.addEventListener('click', (event) => {
      if (event.target === popup) closePopup(popup);
    });
  });

  const descriptionEl = document.getElementById('roomAdminDescription');
  if (descriptionEl && descriptionCountEl) {
    descriptionEl.addEventListener('input', () => {
      descriptionCountEl.textContent = String(descriptionEl.value.length);
    });
  }

  if (lightAlwaysOnEl) {
    lightAlwaysOnEl.addEventListener('change', syncLightDependentToggles);
  }

  detectionEnabledEl?.addEventListener('change', syncDetectionControls);
  detectionPipelineEl?.addEventListener('change', syncDetectionControls);

  lightIntensityEl?.addEventListener('input', syncLightIntensityLabel);
  // `change` fires once the slider is released.
  lightIntensityEl?.addEventListener('change', () => applyLightIntensity(Number(lightIntensityEl.value)));

  onAuthChange(() => {
    refreshNameAffordance();
    if (modalEl.classList.contains('active')) syncDisabledStates();
  });
}

function isAdminFor(streamName) {
  const user = getBackendUser();
  return Array.isArray(user?.rooms_admin) && user.rooms_admin.includes(streamName);
}

// Permissions come from the user's profile. When they are missing (older backend) every
// control stays enabled and the backend remains the one enforcing them.
function adminPermissions() {
  if (!activeStream) return null;
  const all = getBackendUser()?.room_admin_permissions;
  return all && typeof all === 'object' ? (all[activeStream.name] || null) : null;
}

function canWrite(permission) {
  const permissions = adminPermissions();
  if (!permissions) return true;
  return permissions[permission] === true;
}

function refreshNameAffordance() {
  const isAdmin = Boolean(activeStream && isAdminFor(activeStream.name));
  if (gearBtn) gearBtn.hidden = !isAdmin;
  if (roomNameEl) {
    roomNameEl.classList.toggle('room-name--admin', isAdmin);
    if (isAdmin) {
      roomNameEl.title = 'edit room settings';
    } else {
      roomNameEl.removeAttribute('title');
    }
  }
}

function setFanSpeedVisible(visible) {
  if (fanSpeedStatEl) fanSpeedStatEl.hidden = !visible;
  if (cpuTempToggleBtn) {
    cpuTempToggleBtn.setAttribute('aria-expanded', visible ? 'true' : 'false');
    cpuTempToggleBtn.title = visible ? 'hide fan speed' : 'show fan speed';
  }
}

export function setActiveRoomForAdmin(stream, onStreamUpdated = null) {
  init();
  activeStream = stream || null;
  onUpdated = typeof onStreamUpdated === 'function' ? onStreamUpdated : null;
  refreshNameAffordance();
}

export function clearActiveRoomForAdmin() {
  activeStream = null;
  onUpdated = null;
  refreshNameAffordance();
  if (modalEl?.classList.contains('active')) {
    closeModal();
  }
}

function setFieldFromStream(field, stream) {
  const el = document.getElementById(field.id);
  if (!el) return;
  const value = stream ? stream[field.responseKey] : undefined;

  if (field.type === 'bool') {
    if (el instanceof HTMLInputElement && el.type === 'checkbox') {
      el.checked = value === true;
    } else {
      el.value = value === true ? 'true' : 'false';
    }
  } else if (field.type === 'int' || field.type === 'nullable_int') {
    el.value = Number.isFinite(Number(value)) && value !== null && value !== undefined && value !== ''
      ? String(value)
      : '';
  } else {
    el.value = (value === null || value === undefined) ? '' : String(value);
  }
}

function populateForm(stream) {
  FIELDS.forEach((f) => setFieldFromStream(f, stream));
  DETECTION_FIELDS.forEach((f) => setDetectionFieldFromStream(f));
  syncDetectionControls();
  if (descriptionCountEl) {
    const descEl = document.getElementById('roomAdminDescription');
    descriptionCountEl.textContent = String(descEl?.value?.length ?? 0);
  }
  if (lightIntensityEl) {
    const intensity = Number(stream?.light_intensity);
    lightIntensityEl.value = String(Number.isFinite(intensity) && stream?.light_intensity !== null ? intensity : 100);
    syncLightIntensityLabel();
  }
  syncDisabledStates();
  syncRaspberryLightControls();
  renderRaspberryStats(stream);
  setFeedback('', null);
}

function isRaspberryPi() {
  return activeStream?.feeder_type === 'RASPBERRY_PI';
}

function syncRaspberryLightControls() {
  if (!raspberryLightControlsEl) return;
  raspberryLightControlsEl.hidden = !isRaspberryPi();
}

function renderRaspberryStats(stream) {
  if (!raspberryStatsEl) return;
  if (!isRaspberryPi()) {
    raspberryStatsEl.hidden = true;
    return;
  }
  raspberryStatsEl.hidden = false;

  const lastSeen = stream?.feeder_last_seen_at ? new Date(stream.feeder_last_seen_at) : null;
  const ageMs = lastSeen ? Date.now() - lastSeen.getTime() : null;
  const online = ageMs !== null && ageMs < ONLINE_THRESHOLD_MS;

  if (cpuTempEl) {
    const temp = stream?.cpu_temperature;
    cpuTempEl.classList.remove('room-admin-stat-value--warn', 'room-admin-stat-value--hot');
    if (cpuTempWarnEl) cpuTempWarnEl.hidden = true;

    // The last reading of an offline feeder is stale, so don't show it.
    if (!online) {
      cpuTempEl.textContent = '-';
    } else if (temp === null || temp === undefined) {
      cpuTempEl.textContent = 'null';
    } else {
      const n = Number(temp);
      cpuTempEl.textContent = `${temp}°C`;
      if (n > 80) {
        cpuTempEl.classList.add('room-admin-stat-value--hot');
        if (cpuTempWarnEl) cpuTempWarnEl.hidden = false;
      } else if (n > 60) {
        cpuTempEl.classList.add('room-admin-stat-value--warn');
      }
    }
  }

  if (fanSpeedEl) {
    const fan = stream?.fan_speed_percent;
    fanSpeedEl.textContent = (fan === null || fan === undefined) ? 'null' : `${fan}%`;
  }

  if (statusEl) {
    statusEl.classList.toggle('room-admin-stat-value--online', online);
    statusEl.classList.toggle('room-admin-stat-value--offline', !online);
    statusDotEl?.classList.toggle('room-admin-status-dot--online', online);
    statusDotEl?.classList.toggle('room-admin-status-dot--offline', !online);

    if (online) {
      statusEl.textContent = 'online';
      statusEl.title = '';
    } else if (lastSeen) {
      statusEl.textContent = `offline · seen ${formatTimeAgo(ageMs)}`;
      statusEl.title = lastSeen.toLocaleString(DATE_LOCALE);
    } else {
      statusEl.textContent = 'offline · never seen';
      statusEl.title = '';
    }
  }
}

function formatTimeAgo(ms) {
  const units = [
    ['year', 365 * 24 * 3600],
    ['month', 30 * 24 * 3600],
    ['week', 7 * 24 * 3600],
    ['day', 24 * 3600],
    ['hour', 3600],
    ['minute', 60],
  ];
  const seconds = Math.max(0, Math.floor(ms / 1000));
  for (const [unit, size] of units) {
    const n = Math.floor(seconds / size);
    if (n >= 1) return `${n} ${unit}${n === 1 ? '' : 's'} ago`;
  }
  return 'just now';
}

function setFeedback(message, kind) {
  if (!feedbackEl) return;
  if (!message) {
    feedbackEl.style.display = 'none';
    feedbackEl.textContent = '';
    feedbackEl.classList.remove('room-admin-feedback--error', 'room-admin-feedback--success');
    return;
  }
  feedbackEl.textContent = message;
  feedbackEl.style.display = 'block';
  feedbackEl.classList.toggle('room-admin-feedback--error', kind === 'error');
  feedbackEl.classList.toggle('room-admin-feedback--success', kind === 'success');
}

function readFieldValue(field) {
  const el = document.getElementById(field.id);
  if (!el) return undefined;
  if (field.type === 'bool') {
    if (el instanceof HTMLInputElement && el.type === 'checkbox') {
      return el.checked === true;
    }
    return el.value === 'true';
  }
  if (field.type === 'int' || field.type === 'nullable_int') {
    const raw = el.value.trim();
    if (raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return Number.NaN;
    return n;
  }
  return el.value.trim();
}

function originalValue(field, stream) {
  if (!stream) return undefined;
  const value = stream[field.responseKey];
  if (field.type === 'bool') return value === true;
  if (field.type === 'int' || field.type === 'nullable_int') {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return value === null || value === undefined ? '' : String(value);
}

function validate(field, value) {
  if (value === undefined) return null;
  if (field.type === 'string') {
    const trimmed = (value || '').trim();
    if (field.required && trimmed.length === 0) {
      return `${labelFor(field)} cannot be empty`;
    }
    if (field.maxLength && trimmed.length > field.maxLength) {
      return `${labelFor(field)} must be at most ${field.maxLength} characters`;
    }
  } else if (field.type === 'int' || field.type === 'nullable_int') {
    if (value === null) return null;
    if (Number.isNaN(value)) return `${labelFor(field)} must be a whole number`;
    if (field.min !== undefined && value < field.min) {
      return `${labelFor(field)} must be ≥ ${field.min}`;
    }
    if (field.max !== undefined && value > field.max) {
      return `${labelFor(field)} must be ≤ ${field.max}`;
    }
  }
  return null;
}

function labelFor(field) {
  const labelEl = document.querySelector(`label[for="${field.id}"]`);
  return labelEl?.textContent?.trim() || field.responseKey;
}

function buildPayload() {
  if (!activeStream) return { payload: {}, error: null };
  const payload = {};

  for (const field of FIELDS) {
    if (field.raspberryOnly && !isRaspberryPi()) continue;
    const current = readFieldValue(field);
    if (current === undefined) continue;

    const validationError = validate(field, current);
    if (validationError) {
      return { payload, error: validationError };
    }

    const original = originalValue(field, activeStream);

    let changed;
    if (field.type === 'bool') {
      changed = current !== original;
    } else if (field.type === 'int') {
      // Skip blank int that was also blank originally.
      if (current === null && (original === null || original === undefined)) {
        changed = false;
      } else {
        changed = current !== original;
      }
    } else {
      const currStr = (current ?? '').toString();
      const origStr = (original ?? '').toString();
      changed = currStr !== origStr;
    }

    if (!changed) continue;

    // Skip blank regular ints (we don't send null/empty values to clear fields).
    if (field.type === 'int' && current === null) continue;

    const requestKey = REQUEST_FIELD_NAME[field.responseKey] || field.responseKey;
    payload[requestKey] = current;
  }

  const windowError = applyHourWindow(payload, 'no-feeding', 'roomAdminNightStart', 'roomAdminNightEnd')
    || applyHourWindow(payload, 'light always on', 'roomAdminLightOnStart', 'roomAdminLightOnEnd');
  if (windowError) {
    return { payload, error: windowError };
  }

  return { payload, error: null };
}

// Paired validation for an hour window; if either hour changed, both are sent so
// the API receives them as a pair. Returns an error message, or null when valid.
function applyHourWindow(payload, label, startId, endId) {
  const startField = FIELDS.find(f => f.id === startId);
  const endField = FIELDS.find(f => f.id === endId);
  if (!startField || !endField) return null;

  const startVal = readFieldValue(startField);
  const endVal = readFieldValue(endField);
  const startIsNull = startVal === null;
  const endIsNull = endVal === null;
  if (startIsNull !== endIsNull) {
    return `both ${label} start and end hour must be set, or both must be empty`;
  }
  if (!startIsNull && startVal === endVal) {
    return `${label} start and end hour must be different`;
  }

  const startKey = REQUEST_FIELD_NAME[startField.responseKey] || startField.responseKey;
  const endKey = REQUEST_FIELD_NAME[endField.responseKey] || endField.responseKey;
  if (startKey in payload || endKey in payload) {
    payload[startKey] = startVal;
    payload[endKey] = endVal;
  }
  return null;
}

function detectionConfig() {
  return (activeStream && activeStream.detection_config) || {};
}

function streamDetectionValue(field) {
  return field.fromRoom ? activeStream?.[field.key] : detectionConfig()[field.key];
}

// `showWhen` is 'enabled' or the pipeline that has to be selected.
function detectionShows(showWhen) {
  if (!showWhen) return true;
  if (detectionEnabledEl?.checked !== true) return false;
  return showWhen === 'enabled' || detectionPipelineEl?.value === showWhen;
}

// Local detection is tuned by the room's settings. The central server's are fixed, so they
// only show read-only.
function syncDetectionControls() {
  if (detectionPipelineFieldEl) detectionPipelineFieldEl.hidden = !detectionShows('enabled');
  if (detectionAdvancedEl) detectionAdvancedEl.hidden = !detectionShows('LOCAL');
  if (centralDetectionSettingsEl) centralDetectionSettingsEl.hidden = !detectionShows('CENTRAL_SERVER');
}

function setDetectionFieldFromStream(field) {
  const el = document.getElementById(field.id);
  if (!el) return;
  const value = streamDetectionValue(field);
  if (field.type === 'bool') {
    el.checked = value === true;
  } else {
    el.value = (value === null || value === undefined || value === '') ? '' : String(value);
  }
}

function detectionLabelFor(field) {
  const labelEl = document.querySelector(`label[for="${field.id}"]`);
  return labelEl?.textContent?.trim() || field.key;
}

function readDetectionValue(field) {
  const el = document.getElementById(field.id);
  if (!el) return undefined;
  if (field.type === 'bool') return el.checked === true;
  if (field.type === 'string') return el.value;
  const raw = el.value.trim();
  if (raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return Number.NaN;
  if (field.type === 'int' && !Number.isInteger(n)) return Number.NaN;
  return n;
}

function originalDetectionValue(field) {
  const value = streamDetectionValue(field);
  if (field.type === 'bool') return value === true;
  if (field.type === 'string') return value ?? '';
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function validateDetection(field, value) {
  if (value === undefined || value === null) return null;
  if (field.type === 'bool' || field.type === 'string') return null;
  if (Number.isNaN(value)) return `${detectionLabelFor(field)} must be a number`;
  if (field.type === 'int' && !Number.isInteger(value)) {
    return `${detectionLabelFor(field)} must be a whole number`;
  }
  if (field.exclusiveMin !== undefined && value <= field.exclusiveMin) {
    return `${detectionLabelFor(field)} must be > ${field.exclusiveMin}`;
  }
  if (field.min !== undefined && value < field.min) {
    return `${detectionLabelFor(field)} must be ≥ ${field.min}`;
  }
  if (field.max !== undefined && value > field.max) {
    return `${detectionLabelFor(field)} must be ≤ ${field.max}`;
  }
  return null;
}

function buildDetectionPayload() {
  if (!activeStream) return { payload: {}, error: null };
  const payload = {};

  for (const field of DETECTION_FIELDS) {
    if (!detectionShows(field.showWhen)) continue;
    const current = readDetectionValue(field);
    if (current === undefined) continue;

    const validationError = validateDetection(field, current);
    if (validationError) {
      return { payload, error: validationError };
    }

    // Numeric fields can't be cleared: skip blanks instead of sending null.
    if (field.type !== 'bool' && current === null) continue;

    if (current !== originalDetectionValue(field)) {
      payload[field.key] = current;
    }
  }

  return { payload, error: null };
}

function setSubmitting(submitting) {
  submittingState = submitting;
  if (saveBtn) {
    saveBtn.disabled = submitting;
    saveBtn.textContent = submitting ? '[saving...]' : '[save changes]';
  }
  if (cancelBtn) cancelBtn.disabled = submitting;
  syncDisabledStates();
}

// Every control inside a [data-permission] wrapper is disabled when the admin lacks that
// permission; the wrapper carries the explanation shown on hover.
function syncDisabledStates() {
  if (!modalEl) return;
  modalEl.querySelectorAll('[data-permission]').forEach((wrapper) => {
    const locked = !canWrite(wrapper.dataset.permission);
    wrapper.classList.toggle('room-admin-locked', locked);
    if (locked) {
      wrapper.title = NO_PERMISSION_MESSAGE;
    } else {
      wrapper.removeAttribute('title');
    }
    const controls = wrapper.matches('input, select, textarea, button')
      ? [wrapper]
      : wrapper.querySelectorAll('input, select, textarea, button');
    controls.forEach((control) => {
      control.disabled = locked || (submittingState && control.type === 'button');
    });
  });
  syncTurnOffLightDisabled();
  syncLightDependentToggles();
}

function syncTurnOffLightDisabled() {
  if (!turnOffLightBtn) return;
  const lightAlwaysOn = activeStream?.light_always_on === true;
  turnOffLightBtn.disabled = submittingState || lightAlwaysOn || !canWrite('write_room_turn_off_light');
}

function syncLightDependentToggles() {
  const alwaysOn = lightAlwaysOnEl?.checked === true;
  [
    [lightOnFeedEl, 'write_room_turn_on_light_on_feed'],
    [lightOnSightingEl, 'write_room_turn_on_light_on_cat_sighting'],
  ].forEach(([el, permission]) => {
    if (!el) return;
    el.disabled = submittingState || alwaysOn || !canWrite(permission);
    el.parentElement?.classList.toggle('room-admin-toggle--disabled', alwaysOn);
  });
}

function showSection(section) {
  if (!modalEl) return;
  modalEl.querySelectorAll('.room-admin-tabs .modal-tab').forEach((tab) => {
    const active = tab.dataset.section === section;
    tab.classList.toggle('active', active);
    tab.setAttribute('aria-selected', active ? 'true' : 'false');
  });
  modalEl.querySelectorAll('.room-admin-section').forEach((el) => {
    el.hidden = el.dataset.section !== section;
  });
  if (fieldsEl) fieldsEl.scrollTop = 0;
}

function hasUnsavedChanges() {
  if (!activeStream) return false;
  const roomChanged = FIELDS.some((field) => {
    if (field.raspberryOnly && !isRaspberryPi()) return false;
    const current = readFieldValue(field);
    if (current === undefined) return false;
    return Number.isNaN(current) || current !== originalValue(field, activeStream);
  });
  if (roomChanged) return true;
  return DETECTION_FIELDS.some((field) => {
    if (!detectionShows(field.showWhen)) return false;
    const current = readDetectionValue(field);
    if (current === undefined) return false;
    return Number.isNaN(current) || current !== originalDetectionValue(field);
  });
}

// Save and cancel only show up once something has been edited.
function syncDirtyState() {
  if (!actionsEl) return;
  actionsEl.hidden = !submittingState && !hasUnsavedChanges();
}

function isPopupOpen(popup) {
  return popup?.classList.contains('active') === true;
}

function openPopup(popup) {
  if (!popup || isPopupOpen(popup)) return;
  popup.classList.add('active');
  modalOpen();
}

function closePopup(popup) {
  if (!isPopupOpen(popup)) return;
  popup.classList.remove('active');
  modalClose();
}

function showToast(message) {
  if (!toastEl) return;
  toastEl.textContent = message;
  toastEl.classList.add('active');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toastEl.classList.remove('active');
    toastTimer = null;
  }, TOAST_DURATION_MS);
}

// Closing with edits pending asks whether to save them first.
function requestClose() {
  if (!modalEl?.classList.contains('active') || submittingState) return;
  if (hasUnsavedChanges()) {
    openPopup(unsavedModalEl);
    return;
  }
  closeModal();
}

// After a one-off action (light, cooldown, silent feed) succeeds: close the modal, report
// the result and pull the fresh room state so the page reflects it.
async function finishAction(successMessage) {
  showToast(successMessage);
  requestClose();
  await reloadActiveRoom();
}

function syncLightIntensityLabel() {
  if (lightIntensityEl && lightIntensityValueEl) {
    lightIntensityValueEl.textContent = `${lightIntensityEl.value}%`;
  }
}

// Saves the new intensity and turns the light on with it, so the light dims or brightens
// right away. Releases that happen while a request is in flight collapse into the latest one.
async function applyLightIntensity(value) {
  if (!activeStream || !Number.isInteger(value)) return;
  if (lightIntensitySending) {
    lightIntensityPending = value;
    return;
  }
  if (value === activeStream.light_intensity) return;

  lightIntensitySending = true;
  setFeedback('', null);
  const roomName = encodeURIComponent(activeStream.name);
  try {
    const saved = await sendAdminPatch(`${apiUrl}/rooms/${roomName}`, { light_intensity: value });
    if (!saved) {
      if (lightIntensityEl) lightIntensityEl.value = String(activeStream.light_intensity ?? 100);
      syncLightIntensityLabel();
      return;
    }
    activeStream.light_intensity = value;
    const res = await authFetch(`${apiUrl}/rooms/${roomName}/turn-on-light`, { method: 'PATCH' });
    if (!res.ok) {
      setFeedback('intensity saved, but the light could not be updated', 'error');
    }
  } catch (err) {
    console.warn('Room admin light intensity failed:', err);
    setFeedback('network error, please try again', 'error');
  } finally {
    lightIntensitySending = false;
    const pending = lightIntensityPending;
    lightIntensityPending = null;
    if (pending !== null) applyLightIntensity(pending);
  }
}

async function handleLightAction(actionPath, successMessage) {
  if (!activeStream) return;
  setFeedback('', null);
  setSubmitting(true);
  try {
    const res = await authFetch(`${apiUrl}/rooms/${encodeURIComponent(activeStream.name)}/${actionPath}`, {
      method: 'PATCH',
    });
    let data = null;
    try { data = await res.json(); } catch (_) { data = null; }
    if (!res.ok) {
      const message = data?.error || data?.detail || `request failed (${res.status})`;
      setFeedback(message, 'error');
      return;
    }
    setSubmitting(false);
    await finishAction(successMessage);
  } catch (err) {
    console.warn(`Room admin ${actionPath} failed:`, err);
    setFeedback('network error, please try again', 'error');
  } finally {
    setSubmitting(false);
  }
}

async function handleResetCooldown() {
  if (!activeStream) return;
  setFeedback('', null);
  setSubmitting(true);
  try {
    const res = await authFetch(`${apiUrl}/rooms/${encodeURIComponent(activeStream.name)}/reset-feed-cooldown`, {
      method: 'PATCH',
    });
    let data = null;
    try { data = await res.json(); } catch (_) { data = null; }
    if (!res.ok) {
      const message = data?.error || data?.detail || `request failed (${res.status})`;
      setFeedback(message, 'error');
      return;
    }
    setSubmitting(false);
    await finishAction('feed cooldown reset');
  } catch (err) {
    console.warn('Room admin reset-feed-cooldown failed:', err);
    setFeedback('network error, please try again', 'error');
  } finally {
    setSubmitting(false);
  }
}

// Dispenses one portion that skips the cooldown and never shows up in the chat, the
// leaderboard or the feed history. The backend still logs it for the room's audit trail.
async function handleSilentFeed() {
  if (!activeStream) return;
  setFeedback('', null);
  setSubmitting(true);
  if (silentFeedBtn) silentFeedBtn.textContent = '[dispensing...]';
  try {
    const res = await authFetch(`${apiUrl}/rooms/${encodeURIComponent(activeStream.name)}/admin-feed`, {
      method: 'POST',
    });
    let data = null;
    try { data = await res.json(); } catch (_) { data = null; }
    if (!res.ok) {
      const message = data?.error || data?.message || data?.detail || `request failed (${res.status})`;
      setFeedback(message, 'error');
      return;
    }
    if (silentFeedBtn) silentFeedBtn.textContent = '[dispense food]';
    setSubmitting(false);
    await finishAction('portion dispensed silently');
  } catch (err) {
    console.warn('Room admin admin-feed failed:', err);
    setFeedback('network error, please try again', 'error');
  } finally {
    if (silentFeedBtn) silentFeedBtn.textContent = '[dispense food]';
    setSubmitting(false);
  }
}

async function sendAdminPatch(url, payload) {
  const res = await authFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  let data = null;
  try { data = await res.json(); } catch (_) { data = null; }
  if (!res.ok) {
    const message = data?.error || data?.detail || `request failed (${res.status})`;
    setFeedback(message, 'error');
    return false;
  }
  return true;
}

async function handleSubmit(event) {
  event.preventDefault();
  if (!activeStream) return;

  setFeedback('', null);
  const { payload, error } = buildPayload();
  if (error) {
    setFeedback(error, 'error');
    return;
  }

  const { payload: detectionPayload, error: detectionError } = buildDetectionPayload();
  if (detectionError) {
    setFeedback(detectionError, 'error');
    return;
  }

  const hasRoomChanges = Object.keys(payload).length > 0;
  const hasDetectionChanges = Object.keys(detectionPayload).length > 0;
  if (!hasRoomChanges && !hasDetectionChanges) {
    setFeedback('no changes to save', 'error');
    return;
  }

  const roomName = encodeURIComponent(activeStream.name);
  setSubmitting(true);
  try {
    if (hasRoomChanges) {
      const ok = await sendAdminPatch(`${apiUrl}/rooms/${roomName}`, payload);
      if (!ok) return;
    }

    if (hasDetectionChanges) {
      const ok = await sendAdminPatch(`${apiUrl}/rooms/${roomName}/detection-config`, detectionPayload);
      if (!ok) return;
    }

    setSubmitting(false);
    closeModal();
    showToast('changes saved');
    await reloadActiveRoom();
  } catch (err) {
    console.warn('Room admin save failed:', err);
    setFeedback('network error, please try again', 'error');
  } finally {
    setSubmitting(false);
  }
}

async function reloadActiveRoom() {
  if (!activeStream?.name) return;
  try {
    const res = await authFetch(`${apiUrl}/rooms/${encodeURIComponent(activeStream.name)}`);
    let data = null;
    try { data = await res.json(); } catch (_) { data = null; }
    if (!res.ok || !data || typeof data !== 'object') return;
    activeStream = { ...activeStream, ...data };
    if (onUpdated) {
      try { onUpdated(activeStream); } catch (_) { /* noop */ }
    }
  } catch (err) {
    console.warn('Room admin reload room failed:', err);
  }
}

async function refreshRaspberryStats() {
  const roomName = activeStream?.name;
  if (!roomName || !isRaspberryPi()) return;
  try {
    const res = await authFetch(`${apiUrl}/rooms/${encodeURIComponent(roomName)}`);
    let data = null;
    try { data = await res.json(); } catch (_) { data = null; }
    // The room may have changed while the request was in flight.
    if (res.ok && data && typeof data === 'object' && activeStream?.name === roomName) {
      STATS_KEYS.forEach((key) => {
        if (key in data) activeStream[key] = data[key];
      });
    }
  } catch (err) {
    console.warn('Room admin stats refresh failed:', err);
  }
  // Re-render even on failure so the "seen N ago" age keeps advancing.
  if (modalEl?.classList.contains('active')) renderRaspberryStats(activeStream);
}

function startStatsRefresh() {
  stopStatsRefresh();
  if (!isRaspberryPi()) return;
  refreshRaspberryStats();
  statsRefreshTimer = setInterval(refreshRaspberryStats, STATS_REFRESH_MS);
}

function stopStatsRefresh() {
  if (statsRefreshTimer) {
    clearInterval(statsRefreshTimer);
    statsRefreshTimer = null;
  }
}

function openModal() {
  init();
  if (!modalEl || !activeStream) return;
  if (!isAdminFor(activeStream.name)) return;
  if (modalEl.classList.contains('active')) return;
  if (titleEl) titleEl.textContent = activeStream.display_name || activeStream.name;
  setFanSpeedVisible(false);
  populateForm(activeStream);
  setSubmitting(false);
  showSection(DEFAULT_SECTION);
  syncDirtyState();
  modalEl.classList.add('active');
  modalOpen();
  startStatsRefresh();
  const firstInput = document.getElementById('roomAdminDisplayName');
  if (firstInput && !firstInput.disabled) {
    requestAnimationFrame(() => firstInput.focus());
  }
}

function closeModal() {
  if (!modalEl) return;
  if (!modalEl.classList.contains('active')) return;
  closePopup(unsavedModalEl);
  closePopup(noPermissionModalEl);
  closePopup(silentFeedModalEl);
  stopStatsRefresh();
  modalEl.classList.remove('active');
  modalClose();
}
